import type { ReactNode } from 'react'
import { Pin02Icon } from '@systemeio/icons'
import DOMPurify from 'isomorphic-dompurify'
import { useTranslation } from 'next-i18next'
import { ConditionalWrapper } from 'shared/components/conditional-wrapper'
import { usePublicationRelativeDate } from 'shared/hooks/use-community-relative-date'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import { UserInfoPopover } from 'modules/community/components/user-info-popover/user-info-popover'

interface CommentHeaderProps {
  userName: string
  userId: number
  isCommunity?: boolean
  commentTimestamp: number
  wasEdited: boolean
  isPinned: boolean
  highlightedAuthorHTML?: string
}

export const CommentHeader = ({
  userName,
  userId,
  isCommunity = false,
  commentTimestamp,
  wasEdited,
  isPinned,
  highlightedAuthorHTML,
}: CommentHeaderProps): ReactNode => {
  const { t } = useTranslation()
  const formattedTimestamp = useFormatDateTime(commentTimestamp)
  const relativeLabel = usePublicationRelativeDate(commentTimestamp)

  return (
    <div className="mr-8 flex flex-col items-start">
      <div className="flex items-center gap-3">
        <ConditionalWrapper
          when={isCommunity}
          wrapper={children => <UserInfoPopover userId={userId}>{children}</UserInfoPopover>}
        >
          <span
            className="max-w-[150px] truncate text-text-primary sm:max-w-sm lg:max-w-lg"
            title={userName}
            dangerouslySetInnerHTML={{
              __html: highlightedAuthorHTML ? DOMPurify.sanitize(highlightedAuthorHTML) : userName,
            }}
          />
        </ConditionalWrapper>
        {isPinned && (
          <p className="flex items-center gap-1 text-sm text-text-tertiary">
            <Pin02Icon className="size-5" /> <span>{t('comments.pinned')}</span>
          </p>
        )}
      </div>

      <div className="mt-2 text-[15px]/5 text-text-tertiary" title={formattedTimestamp?.title}>
        {relativeLabel}
        {wasEdited && ` • (${t('comments.state.edited')})`}
      </div>
    </div>
  )
}
